import { motion } from "framer-motion";
import { Flame, Wallet } from "lucide-react";

const reveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

function BarsVisual() {
  return (
    <div className="flex h-44 items-end justify-center gap-6">
      {[
        { label: "kcal", value: "86%", color: "bg-emerald-500", h: "86%" },
        { label: "budget", value: "62%", color: "bg-sky-500", h: "62%" },
      ].map((b) => (
        <div key={b.label} className="flex w-20 flex-col items-center gap-3">
          <div className="flex h-32 w-full items-end overflow-hidden rounded-xl bg-white/[0.04]">
            <motion.div
              initial={{ height: 0 }}
              whileInView={{ height: b.h }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
              className={`w-full ${b.color} opacity-80`}
            />
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            {b.label} · {b.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function MatrixVisual() {
  const cells = [
    { icon: Flame, tint: "text-emerald-400" },
    null,
    { icon: Wallet, tint: "text-sky-400" },
    null,
    { icon: Wallet, tint: "text-sky-400" },
    { icon: Flame, tint: "text-emerald-400" },
    null,
    { icon: Flame, tint: "text-emerald-400" },
    { icon: Wallet, tint: "text-sky-400" },
    null,
    { icon: Flame, tint: "text-emerald-400" },
    null,
  ];
  return (
    <div className="mx-auto grid w-56 grid-cols-4 gap-2">
      {cells.map((c, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: i * 0.05 }}
          className="flex aspect-square items-center justify-center rounded-lg border border-white/5 bg-white/[0.03]"
        >
          {c && <c.icon className={`h-4 w-4 ${c.tint}`} />}
        </motion.div>
      ))}
    </div>
  );
}

function LineVisual() {
  return (
    <svg viewBox="0 0 300 120" className="mx-auto w-full max-w-xs">
      <motion.path
        d="M0 90 C 40 80, 60 40, 100 55 S 160 95, 200 60 S 270 20, 300 30"
        fill="none"
        stroke="#10B981"
        strokeWidth="2"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, ease: "easeInOut" }}
      />
      <motion.path
        d="M0 100 C 50 95, 80 70, 120 78 S 180 40, 220 50 S 280 35, 300 15"
        fill="none"
        stroke="#0EA5E9"
        strokeWidth="2"
        strokeDasharray="4 4"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, ease: "easeInOut", delay: 0.3 }}
      />
    </svg>
  );
}

function RulesVisual() {
  return (
    <div className="mx-auto flex w-full max-w-xs flex-col gap-3">
      {[
        { rule: "Gym day → +$20 dining budget", on: true },
        { rule: "Rest day → auto-save $15", on: true },
        { rule: "Over calories → freeze treats fund", on: false },
      ].map((r, i) => (
        <motion.div
          key={r.rule}
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: i * 0.15 }}
          className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.03] px-4 py-3"
        >
          <span className="text-xs text-neutral-300">{r.rule}</span>
          <span
            className={`relative h-5 w-9 rounded-full transition-colors ${r.on ? "bg-emerald-500/80" : "bg-white/10"}`}
          >
            <span
              className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${r.on ? "left-[18px]" : "left-0.5"}`}
            />
          </span>
        </motion.div>
      ))}
    </div>
  );
}

const CHAPTERS = [
  {
    num: "01",
    title: "The Dual Energy Standard",
    sub: "Treat calories like currency. Spend energy with the same discipline as your portfolio.",
    testId: "chapter-01-card",
    Visual: BarsVisual,
  },
  {
    num: "02",
    title: "Unified Budgeting Matrix",
    sub: "Sync your daily deficit targets alongside monthly liquidity goals in one calm interface.",
    testId: "chapter-02-card",
    Visual: MatrixVisual,
  },
  {
    num: "03",
    title: "Metabolic & Capital Analytics",
    sub: "Correlate your macro ratios with spending habits to identify peak performance windows.",
    testId: "chapter-03-card",
    Visual: LineVisual,
  },
  {
    num: "04",
    title: "Automated Deficit & Savings Rules",
    sub: "Smart triggers that adjust your micro-budget as your calorie expenditure fluctuates.",
    testId: "chapter-04-card",
    Visual: RulesVisual,
  },
];

export default function Chapters() {
  return (
    <section id="chapters" className="py-24 sm:py-32 lg:py-40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.p {...reveal} className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-emerald-400/90">
          The Manifesto
        </motion.p>
        <motion.h2 {...reveal} className="mb-20 max-w-3xl text-2xl font-bold leading-[1.15] tracking-tight sm:text-4xl lg:text-5xl">
          Two ledgers. One discipline. Zero guilt spreadsheets.
        </motion.h2>

        <div className="flex flex-col gap-24 lg:gap-32">
          {CHAPTERS.map((c, i) => (
            <motion.article
              key={c.num}
              data-testid={c.testId}
              {...reveal}
              className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16"
            >
              <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-emerald-400/90">
                  Chapter {c.num}
                </p>
                <h3 className="mt-4 text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                  {c.title}
                </h3>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-neutral-300">
                  {c.sub}
                </p>
              </div>
              <div
                className={`rounded-3xl border border-white/5 bg-[#111318] p-8 transition-colors duration-500 hover:border-emerald-500/30 sm:p-12 lg:col-span-6 ${
                  i % 2 === 1 ? "lg:order-1" : ""
                }`}
              >
                <c.Visual />
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
